import React, { useMemo } from 'react';

import { IconButton } from '@material-ui/core';
import clsx from 'clsx';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { ReactComponent as MoreIcon } from 'assets/icons/more.svg';
import ContextMenu from 'components/ContextMenu/ContextMenu';
import Table from 'components/Table';
import type { AssetProcessingActivityReviewRow } from 'config/api/ropaReviews/ropaReviews.types';
import processing_activities_messages from 'messages/processing_activities_messages';
import {
  getDpiaBadgeClass,
  resolveApaDpiaDisplayLabel,
  resolveDpiaBadgeStatus,
} from 'pages/ProcessingActivities/_shared/apaDpiaStatus.helpers';
import { canShowOwnerDpiaAction } from 'pages/ProcessingActivities/_shared/canShowOwnerDpiaAction';
import DpiaAvailableIndicator from 'pages/ProcessingActivities/_shared/DpiaAvailableIndicator';
import { canShowDpiaHistoryAction } from 'pages/ProcessingActivities/_shared/dpiaCaseActions';
import type { TableDataMappingRow } from 'types/Table';

import useStyles from '../../ProcessingActivitiesAssetReviewsPage/ProcessingActivitiesAssetReviewsPage.styles';

type ReviewStatusMessageKey = keyof typeof processing_activities_messages.asset_ropa.review_status;

type Props = {
  canEdit?: boolean;
  onOpenDpia?: (row: AssetProcessingActivityReviewRow) => void;
  onOpenEditor: (row: AssetProcessingActivityReviewRow, mode: 'view' | 'edit') => void;
  onRemove: (row: AssetProcessingActivityReviewRow) => void;
  onViewDpiaHistory?: (row: AssetProcessingActivityReviewRow) => void;
  rows: AssetProcessingActivityReviewRow[];
  showReviewStatus?: boolean;
};

const formatDate = (value: string) => {
  try {
    return format(new Date(value), 'yyyy-MM-dd');
  } catch {
    return value;
  }
};

const getCompletenessBadgeClass = (status: string | undefined, styles: ReturnType<typeof useStyles>) => {
  if (status === 'complete') {
    return styles.statusBadgeSuccess;
  }

  return styles.statusBadgeWarn;
};

const getReviewStatusDotClass = (status: AssetProcessingActivityReviewRow['reviewStatus'], styles: ReturnType<typeof useStyles>) => {
  switch (status) {
    case 'accepted':
      return styles.statusDotSuccess;
    case 'submitted':
      return styles.statusDotInfo;
    case 'clarification_requested':
      return styles.statusDotDanger;
    default:
      return styles.statusDotWarning;
  }
};

const OwnerWorkspaceProcessingTable: React.FC<Props> = ({
  canEdit = true,
  onOpenDpia,
  onOpenEditor,
  onRemove,
  onViewDpiaHistory,
  rows,
  showReviewStatus = false,
}) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const messages = processing_activities_messages.asset_ropa.owner_workspace.table;
  const completenessMessages = processing_activities_messages.asset_ropa.owner_workspace.row_completeness;

  const dataMapping: TableDataMappingRow<AssetProcessingActivityReviewRow>[] = useMemo(() => {
    const columns: TableDataMappingRow<AssetProcessingActivityReviewRow>[] = [
      {
        id: 'processingActivityName',
        label: t(messages.processing_activity),
        get: row => (
          <div>
            <div>{row.processingActivityName}</div>
            {row.processingRole && <div style={{ color: 'inherit', fontSize: '0.75rem', opacity: 0.7 }}>{row.processingRole}</div>}
            {row.hasNewPurposeNotAssessed && (
              <span className={styles.newPurposeBadge}>
                {t(processing_activities_messages.asset_ropa.owner_workspace.new_purpose_badge)}
              </span>
            )}
          </div>
        ),
      },
      {
        id: 'purposesSummary',
        label: t(messages.purposes),
        get: row => row.purposesSummary ?? row.activePurposesCount,
      },
      {
        id: 'completenessStatus',
        label: t(messages.completeness),
        get: row => (
          <span className={clsx(styles.statusBadge, getCompletenessBadgeClass(row.completenessStatus, styles))}>
            {t(completenessMessages[row.completenessStatus === 'complete' ? 'complete' : 'incomplete'])}
          </span>
        ),
      },
    ];

    if (showReviewStatus) {
      columns.push({
        id: 'reviewStatus',
        label: t(messages.review_status),
        get: row => (
          <span className={styles.assetSummaryStatus}>
            <span className={clsx(styles.statusDot, getReviewStatusDotClass(row.reviewStatus, styles))} />
            {t(processing_activities_messages.asset_ropa.review_status[row.reviewStatus as ReviewStatusMessageKey])}
          </span>
        ),
      });
    }

    columns.push(
      {
        id: 'updatedAt',
        label: t(messages.last_updated),
        get: row => formatDate(row.updatedAt),
      },
      {
        id: 'dpiaDisplayStatus',
        label: t(messages.dpia),
        get: row => (
          <div>
            <span
              className={clsx(
                styles.dpiaBadge,
                getDpiaBadgeClass(resolveDpiaBadgeStatus(row.dpiaDisplayStatus, { dpiaRequestStatus: row.dpiaRequestStatus }), styles),
              )}
            >
              {resolveApaDpiaDisplayLabel(row.dpiaDisplayStatus, t, { dpiaRequestStatus: row.dpiaRequestStatus })}
            </span>
            <DpiaAvailableIndicator available={row.hasCompletedDpia} />
            {(row.hasPreviousDpias || (row.dpiaCount ?? 0) > 1) && (
              <div className={styles.dpiaPreviousHint}>
                {t(processing_activities_messages.asset_ropa.owner_workspace.previous_dpias_available)}
              </div>
            )}
          </div>
        ),
      },
      {
        id: 'countriesSummary',
        label: t(messages.countries),
        get: row => row.countriesSummary ?? '—',
      },
      {
        id: 'actions',
        blockSorting: true,
        isAddon: true,
        label: '',
        get: row => {
          const rowCanOpen = row.canOpen !== false;
          const rowCanEdit = canEdit && row.canEdit !== false;
          const menuItems = [
            ...(rowCanOpen
              ? [
                  {
                    label: t(processing_activities_messages.asset_ropa.actions.open),
                    onClick: () => onOpenEditor(row, 'view'),
                  },
                ]
              : []),
            ...(rowCanOpen && rowCanEdit
              ? [
                  {
                    label: t(processing_activities_messages.asset_ropa.actions.edit),
                    onClick: () => onOpenEditor(row, 'edit'),
                  },
                ]
              : []),
            ...(onOpenDpia && canShowOwnerDpiaAction(row)
              ? [
                  {
                    label: t(processing_activities_messages.asset_ropa.owner_workspace.actions.open_dpia),
                    onClick: () => onOpenDpia(row),
                  },
                ]
              : []),
            ...(onViewDpiaHistory && canShowDpiaHistoryAction(row)
              ? [
                  {
                    label: t(processing_activities_messages.asset_ropa.owner_workspace.actions.view_dpia_history),
                    onClick: () => onViewDpiaHistory(row),
                  },
                ]
              : []),
            ...(rowCanEdit
              ? [
                  {
                    label: t(processing_activities_messages.asset_ropa.owner_workspace.actions.remove_from_asset),
                    onClick: () => onRemove(row),
                  },
                ]
              : []),
          ];

          if (menuItems.length === 0) {
            return null;
          }

          return (
            <div className={styles.rowActions}>
              <ContextMenu elements={menuItems}>
                <IconButton aria-label={t(processing_activities_messages.asset_ropa.actions.row_menu)} size='small'>
                  <MoreIcon />
                </IconButton>
              </ContextMenu>
            </div>
          );
        },
      },
    );

    return columns;
  }, [canEdit, completenessMessages, messages, onOpenDpia, onOpenEditor, onRemove, onViewDpiaHistory, showReviewStatus, styles, t]);

  return (
    <div className={styles.tableScrollWrapper}>
      <Table
        clickableRow
        data={rows}
        dataMapping={dataMapping}
        onRowClick={(_, row) => {
          if (row.canOpen !== false) {
            onOpenEditor(row, 'view');
          }
        }}
        refetching={false}
        rowAriaLabel={row => t(messages.row_aria_label, { name: row.processingActivityName })}
      />
    </div>
  );
};

export default OwnerWorkspaceProcessingTable;
